import type { ReportsMenu } from "../../shared/reportsmenu.js";
import type { Access } from "../../shared/access.js";
import { closeModal, get, modalOpen, setOrgTimeZone } from "./lib.js";
import type { Permissions, Role } from "../../shared/permissions.js";

export interface Workspace { orgId: string; name: string; code: string; role: Role; available: boolean; closed?: boolean; logoUrl?: string }
export interface Session {
  signedIn: boolean;
  mfaPassed?: boolean;
  needsTwoFactorSetup?: boolean;
  version: string;
  /** The installed commit (first 7 characters), when known. */
  build?: string;
  emailConfigured: boolean;
  user?: { id: string; email: string; firstName: string; lastName: string; isPlatformOwner: boolean; totpEnabled: boolean; language?: string };
  mode?: "none" | "platform" | "organisation";
  supportAccess?: boolean;
  organisation?: {
    /** Inventory is switched on (Settings → Inventory). */
    inventory?: boolean;
    id: string; code: string; name: string; logoUrl: string; logoTransparent?: boolean; industryTemplate: string; features: Record<string, boolean>;
    /** What the organisation makes; the set-up wizard (finished / started) and whether this person is its main Admin. */
    industries?: string[]; setupDone?: boolean; setupStarted?: boolean; mainAdmin?: boolean;
    plmEnabled: boolean; crmEnabled: boolean; crmPlmLink?: boolean; timezone: string; currency: string; adminsEditSetup?: boolean; freightFinance?: boolean; tradingCurrency?: string; teamAgent?: boolean;
    finance?: { customsOn: boolean; lcOn: boolean; whtOn: boolean; quotesOn?: boolean; exportOn?: boolean; profileComplete: boolean; country: string } | null;
    /** Closed: read-only until the deletion date. */
    closed?: { at: string; deleteAfter: string; by: string } | null;
  } | null;
  membership?: { role: Role; userCode: string; permissions: Permissions; plmAccess: boolean; crmAccess: boolean; crmRole?: "salesperson" | "manager" } | null;
  actor?: { role: Role; permissions: Permissions; supportAccess?: boolean; access?: Access } | null;
  workspaces?: Workspace[];
  /** Settings can be changed until then (password or two-factor code entered recently); null when locked. */
  settingsUnlockedUntil?: string | null;
  twoFactorOn?: boolean;
  /** Sign in with Google / Microsoft / Apple: the ones switched on (the buttons to show), the ones this person has
   *  linked, and whether they also have a password. */
  signInProviders?: string[];
  signInLinked?: string[];
  hasPassword?: boolean;
  /** The main Reports menu switch (hidden unless switched back on). */
  reportsMenu?: ReportsMenu;
  /** Legal documents still to agree to in this organisation (shown before anything else). */
  legal?: { terms: boolean; dpa: boolean; version: string; orgName: string; again: boolean } | null;
  /** The Falcon owner in the Console: legal details still missing; closed organisations ready to delete. */
  console?: { legalMissing: string[]; closingDue: Array<{ id: string; name: string; deleteAfter: string }> } | null;
}

export const state: { session: Session } = { session: { signedIn: false, version: "", emailConfigured: false } };

type SessionListener = (before: string | null, after: string | null) => void;
let sessionListener: SessionListener = () => {};
export function onSessionChanged(fn: SessionListener): void {
  sessionListener = fn;
}
const signedInAs = (s: Session) => (s.signedIn && s.mfaPassed ? s.user?.id ?? null : null);

// Tabs of the same browser tell each other when the sign-in changes.
const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("falcon-session") : null;
channel?.addEventListener("message", (e) => {
  const mine = signedInAs(state.session);
  if (mine && e.data?.user !== mine) sessionListener(mine, e.data?.user ?? null);
});

/**
 * Ask the server who is signed in. `expected` = true when this tab itself just signed in,
 * signed out or chose a workspace; otherwise a different person means another tab changed it.
 */
export async function refreshSession(expected = false): Promise<Session> {
  const before = signedInAs(state.session);
  state.session = await get<Session>("/api/session");
  setOrgTimeZone(state.session.organisation?.timezone);
  const after = signedInAs(state.session);
  if (before !== after) {
    if (expected) channel?.postMessage({ user: after });
    else sessionListener(before, after);
  }
  return state.session;
}

export function fullName(u?: { firstName?: string; lastName?: string; email?: string } | null): string {
  return [u?.firstName, u?.lastName].filter(Boolean).join(" ") || u?.email || "";
}

import { noteScroll } from "./helpstash.js";

// ---- Page registry and navigation ------------------------------------------
export interface PageContext { params: Record<string, string>; query: URLSearchParams; path: string }
export type Renderer = (ctx: PageContext) => Promise<void> | void;
interface Route { pattern: RegExp; keys: string[]; render: Renderer }
const routes: Route[] = [];

export function route(path: string, render: Renderer): void {
  const keys: string[] = [];
  const pattern = new RegExp(`^${path.replace(/:([a-zA-Z]+)/g, (_, k) => { keys.push(k); return "([^/]+)"; })}/?$`);
  routes.push({ pattern, keys, render });
}

export function matchRoute(path: string): { render: Renderer; params: Record<string, string> } | null {
  for (const r of routes) {
    const m = r.pattern.exec(path);
    if (m) return { render: r.render, params: Object.fromEntries(r.keys.map((k, i) => [k, decodeURIComponent(m[i + 1])])) };
  }
  return null;
}

let renderHook: () => Promise<void> = async () => {};
export function setRenderHook(fn: () => Promise<void>): void {
  renderHook = fn;
}
export async function navigate(path: string, replace = false): Promise<void> {
  if (modalOpen()) closeModal();
  if (replace) history.replaceState(null, "", path);
  else if (path !== location.pathname + location.search) history.pushState(null, "", path);
  noteScroll(window.scrollY);
  window.scrollTo(0, 0);
  await renderHook();
}
export function rerender(): Promise<void> {
  return renderHook();
}

// ---- Delegated events (no inline handlers, so the strict security policy holds) -----
export type ActionHandler = (el: HTMLElement, ev: Event) => void | Promise<void>;
export type FormHandler = (form: HTMLFormElement, ev: SubmitEvent) => void | Promise<void>;
export const actions: Record<string, ActionHandler> = {};
export const forms: Record<string, FormHandler> = {};
export const changes: Record<string, ActionHandler> = {};

export function onAction(name: string, fn: ActionHandler): void { actions[name] = fn; }
export function onForm(name: string, fn: FormHandler): void { forms[name] = fn; }
export function onChange(name: string, fn: ActionHandler): void { changes[name] = fn; }

/** An organisation feature switch (Settings → Features, or what the organisation makes): only an explicit "off" hides. */
export const featureOn = (key: string): boolean => state.session.organisation?.features?.[key] !== false;
